import { Link, navigate } from 'gatsby';
import React, { FC } from 'react';
import { Check, ShoppingCart, X } from 'react-feather';
import { useDispatch, useSelector } from 'react-redux';
import { useCompanyModules } from '../../hooks/useCompanyModules';
import { SHOP_ROUTES } from '../../routes';
import { GroupsService } from '../../services/groups/Groups';
import {
  addProductsToGroup,
  removeProductsFromGroup,
} from '../../state/group/GroupProductsSlice';
import { addCyodProduct, selectCyodProduct } from '../../state/shop/cyodSlice';
import { addItemToCart } from '../../state/shop/shopSlice';
import { RootState } from '../../state/store';
import { addToast } from '../../state/toast/ToastSlice';
import ListComponent from '../../utils/ListComponent';
import { CompanyModules } from '../../utils/auth/AuthorizationUtility';
import CurrencyFormatter from '../../utils/text/CurrencyFormatter';
import Panel from '../boxes/Panel';
import TextHeader from '../headers/TextHeader';
import Specification from '../lists/Specification';
import Tag from '../tags/Tag';
import Typography from '../typography/Typography';

type ItemProps = {
  categoryItem: Product;
  isCyod: boolean;
  requirements: any;
  selectMode: boolean;
  groupId: number;
  orderingType?: string;
  addToCart?: (selectmode: boolean, product: Product) => void;
};

const ProductCard: FC<ItemProps> = ({
  categoryItem,
  isCyod,
  requirements,
  selectMode,
  groupId,
  orderingType,
  addToCart,
}) => {
  const service = new GroupsService();
  const cyod = useSelector((state: RootState) => state.cyod);
  const cart = useSelector((state: RootState) => state.cart);
  const products = useSelector(
    (state: RootState) => state.groupProducts.products
  );
  const companyId = useSelector(
    (state: RootState) => state.companies.selectedCompany?.id
  );
  const modules = useSelector((state: RootState) => state.auth.modules);
  const dispatch = useDispatch();

  const handleButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    const selectedProducts = Object.values(cyod.selectedProducts);
    const hasCyodModule = useCompanyModules({ module: CompanyModules.CYOD });
    const isCartEmpty = cart.totalItems === 0;
    const hasNoSelectedProducts = selectedProducts.length === 0;
    const hasProductInCategory = selectedProducts.some(
      (product) => product.category === categoryItem.child_name
    );

    const hasRequirements = Object.keys(requirements).includes(
      categoryItem.child_name
    );

    const canAddCyodProduct =
      isCyod && hasCyodModule && isCartEmpty && !hasProductInCategory;

    if (canAddCyodProduct) {
      if (!hasRequirements) {
        console.log('No requirements, adding to cart directly');
        handleAddToCartCyod(false);
        return;
      }
      handleAddToCartCyod(true);
      return;
    }

    if (hasNoSelectedProducts) {
      handleAddToCart();
      return;
    }

    dispatch(
      addToast({
        description:
          'U mag geen CYOD en niet-CYOD combineren.\nCYOD: U mag maximum 1 product per categorie toevoegen.',
        position: 'bottomRight',
        style: 'error',
      })
    );
  };

  // Add normal product to cart
  const handleAddToCart = () => {
    // Restructure the categoryItem data
    const cartItem: CartItem = {
      [categoryItem.id]: {
        // Using product ID as the key
        name: categoryItem.name,
        price: categoryItem.sale_price,
        quantity: 1, // Assuming default quantity is 1 when added to cart
        productInfo: {
          image: `${categoryItem.image[0].filename}`, // Assuming image is available in categoryItem
          slug: `${categoryItem.linkname}`,
          category: `${categoryItem.child_name}`,
        },
      },
    };
    // Add product to shopslice
    dispatch(addItemToCart(cartItem));

    // Add toast to toastslice
    dispatch(
      addToast({
        description: [
          'Aan winkelwagen toegevoegd',
          { product: categoryItem.name },
        ],
        position: 'bottomRight',
        style: 'success',
      })
    );
  };

  // Add CYOD to CYOD slice
  const handleAddToCartCyod = (req: boolean) => {
    const mainProduct = categoryItem;

    const selectedProduct = {
      [mainProduct.id]: {
        category: mainProduct.child_name,
        product: {
          name: mainProduct.name,
          price: mainProduct.sale_price,
          quantity: 1,
          productInfo: {
            image: mainProduct.image[0].filename,
            slug: mainProduct.linkname,
            category: mainProduct.child_name,
          },
        },
        accessories: [],
      },
    };

    if (req) {
      console.log('Navigating to CYOD overview page');
      dispatch(addCyodProduct(selectedProduct));
      navigate(SHOP_ROUTES.CYOD.OVERVIEW(categoryItem.linkname));
    } else {
      // Add product to shopslice
      dispatch(selectCyodProduct(selectedProduct));

      // Add toast to toastslice
      dispatch(
        addToast({
          description: [
            'Aan winkelwagen toegevoegd',
            { product: mainProduct.name },
          ],
          position: 'bottomRight',
          style: 'success',
        })
      );
    }
  };

  const addProduct = async () => {
    // Update database
    await service.addGroupProducts(
      companyId,
      groupId,
      [categoryItem.id],
      categoryItem.child_name
    );

    dispatch(addProductsToGroup([categoryItem]));

    // Toast notif
    dispatch(
      addToast({
        description: 'Het product is toegevoegd.',
        position: 'bottomRight',
        style: 'success',
      })
    );
  };

  const removeProduct = async () => {
    // Update database
    await service.removeGroupProducts(companyId, groupId, [categoryItem.id]);

    dispatch(removeProductsFromGroup([categoryItem.id]));

    // Toast notif
    dispatch(
      addToast({
        description: 'Het product is verwijderd.',
        position: 'bottomRight',
        style: 'success',
      })
    );
  };

  return (
    <Panel
      key={categoryItem.id}
      background="transparent"
      classNames="border hover:border-blue-700 hover:cursor-pointer dark:border-gray-700 dark:hover:border-blue-700"
    >
      {isCyod && orderingType === 'cyod' && (
        <Tag
          style={'cyod'}
          label={'CYOD'}
          extraClasses="absolute right-2 top-2"
        />
      )}

      <Link
        key={categoryItem.id}
        to={SHOP_ROUTES.PRODUCTDETAIL(
          categoryItem.linkname,
          Object.values(categoryItem.image)[0].filename,
          categoryItem.child_name
        )}
      >
        {categoryItem.image &&
          Object.values(categoryItem.image).map((image, index) => (
            <img
              key={index}
              src={`https://shop.cmcgroup.be/assets/upload/product/${image.filename}.jpg`}
              alt="Overview Image"
              className="h-44 w-auto mx-auto mt-10 mb-2 rounded-md"
            />
          ))}
        <div className="w-4/5 mx-auto">
          <div className="mb-5">
            <TextHeader
              title={categoryItem.name}
              subtitle={categoryItem.brand_name}
              type="semibold"
            />
          </div>

          {categoryItem.specs && (
            <ListComponent
              data={Object.values(categoryItem.specs)}
              renderItem={(specification) => (
                <Specification
                  name={specification.specificatie_naam}
                  value={specification.waarde}
                  piece={specification.stuk}
                />
              )}
            />
          )}
        </div>
      </Link>

      <div className="flex justify-between w-4/5 mx-auto my-5">
        <div className="my-auto">
          <Typography tag="span" type="subtitle">
            <CurrencyFormatter
              tag="excl. BTW"
              price={categoryItem.sale_price / 1.21}
            />
          </Typography>
        </div>
        {selectMode ? (
          products.some((product) => product.id === categoryItem.id) ? (
            <button onClick={removeProduct}>
              <Tag
                style={'danger'}
                label={'Verwijderen'}
                icon={<X />}
                extraClasses="hover:bg-red-700 hover:text-white py-1"
              />
            </button>
          ) : (
            <button onClick={addProduct}>
              <Tag
                style={'blue'}
                label={'Toevoegen'}
                icon={<Check />}
                extraClasses="hover:bg-blue-700 hover:text-white py-1"
              />
            </button>
          )
        ) : (
          <button onClick={() => addToCart(selectMode, categoryItem)}>
            <Tag
              style={'blue'}
              label={''}
              icon={<ShoppingCart />}
              extraClasses="hover:bg-blue-700 hover:text-white py-1"
            />
          </button>
        )}
      </div>
    </Panel>
  );
};

export default ProductCard;
